

import MiniSearch from 'minisearch';


const SYNONYM_GROUPS = [
  // Action: execute
  ['run', 'execute', 'start', 'trigger', 'launch', 'kick', 'invoke', 'fire', 'submit'],
  // Action: list/retrieve
  ['list', 'show', 'get', 'fetch', 'display', 'view', 'find', 'search', 'retrieve', 'read'],
  // Action: create
  ['create', 'add', 'make', 'build', 'generate', 'new', 'define', 'register'],
  // Action: delete
  ['delete', 'remove', 'drop', 'destroy', 'clear', 'purge', 'unregister'],
  // Action: update
  ['update', 'edit', 'modify', 'change', 'configure', 'set', 'patch', 'alter'],
  // Action: upload
  ['upload', 'push', 'send', 'ingest', 'import', 'load', 'publish'],
  // Action: download / export
  ['download', 'pull', 'save', 'export', 'write', 'dump'],
  // Action: stop
  ['stop', 'cancel', 'abort', 'terminate', 'kill', 'halt', 'end'],
  // Action: check / status
  ['check', 'status', 'monitor', 'watch', 'track', 'inspect', 'describe'],
  // Entity: job/pipeline
  ['job', 'task', 'pipeline', 'workflow', 'process', 'operation', 'ingestion', 'run'],
  // Entity: data/table
  ['table', 'dataset', 'data', 'schema', 'database', 'db', 'store', 'catalog'],
  // Entity: audience/segment
  ['audience', 'segment', 'group', 'cohort', 'profile', 'user'],
  // Entity: query/SQL
  ['query', 'sql', 'select', 'report', 'statement'],
  // Entity: file/object
  ['file', 'object', 's3', 'bucket', 'storage', 'path', 'folder', 'directory'],
  // Entity: graph
  ['graph', 'network', 'node', 'edge', 'relationship', 'link'],
  // Entity: credential/auth
  ['credential', 'secret', 'key', 'auth', 'token', 'password', 'connection'],
  // Entity: handler/api
  ['handler', 'endpoint', 'api', 'service', 'hook', 'integration'],
  // Entity: sandbox/environment
  ['sandbox', 'environment', 'env', 'workspace', 'context', 'namespace'],
  // Entity: log/history
  ['log', 'history', 'audit', 'event', 'record', 'trace'],
];

// Build a flat word → synonym-set map for O(1) lookup
const SYNONYM_MAP = new Map();
SYNONYM_GROUPS.forEach(group => {
  const set = new Set(group);
  group.forEach(word => SYNONYM_MAP.set(word, set));
});

// ─── Tokenizer ────────────────────────────────────────────────────────────────

const STOPWORDS = new Set([
  'the','a','an','is','in','of','to','for','and','or','with','on','at','by',
  'from','as','be','it','its','that','this','are','was','were','has','have',
  'will','can','may','should','would','could','do','does','did',
  'any','all','some','please','help','want','need','tell','give',
  'just','also','now','then','here','there','via','into','not','if',
  'when','how','what','which','who','where','about','after','before',
  'up','down','over','under','through','during',
  'i','me','my','we','us','our','he','she','they','his','her','their',
  'use','make','let','try','see','know','think',
]);

function tokenize(text) {
  if (!text) return [];
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2')  // camelCase → words
    .replace(/[_-]/g, ' ')                  // snake_case / kebab-case
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 1 && !STOPWORDS.has(w));
}

/**
 * Expand query tokens with domain synonyms.
 * "run job" → tokens [run, job] → expanded [run, execute, start, trigger, ..., job, task, pipeline, ...]
 */
function expandTokens(tokens) {
  const expanded = new Set(tokens);
  tokens.forEach(token => {
    const synonyms = SYNONYM_MAP.get(token);
    if (synonyms) synonyms.forEach(s => expanded.add(s));
  });
  return [...expanded];
}

// ─── TF-IDF (pure JS, no ONNX) ───────────────────────────────────────────────

function buildTfidfIndex(docs) {
  const df = {};
  docs.forEach(doc => {
    new Set(doc.tokens).forEach(token => { df[token] = (df[token] || 0) + 1; });
  });
  const N = Math.max(docs.length, 1);
  const idf = {};
  Object.keys(df).forEach(t => { idf[t] = Math.log((N + 1) / (df[t] + 1)) + 1; });

  const vectors = docs.map(doc => {
    const tf = {};
    doc.tokens.forEach(t => { tf[t] = (tf[t] || 0) + 1; });
    const len = doc.tokens.length || 1;
    const vec = {};
    Object.keys(tf).forEach(t => { vec[t] = (tf[t] / len) * (idf[t] || 0); });
    return { name: doc.name, vec };
  });

  return { idf, vectors };
}

function tfidfScore(queryTokens, docVec, idf) {
  let score = 0;
  const len = queryTokens.length || 1;
  queryTokens.forEach(token => {
    if (docVec[token]) {
      score += (1 / len) * (idf[token] || 0) * docVec[token];
    }
  });
  return score;
}

// ─── Reciprocal Rank Fusion ───────────────────────────────────────────────────

function reciprocalRankFusion(lists, weights, topK, k = 60) {
  const scores = {};
  lists.forEach((list, i) => {
    const w = weights[i] ?? 1;
    list.forEach((name, idx) => {
      scores[name] = (scores[name] || 0) + w / (k + idx + 1);
    });
  });
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topK)
    .map(([name]) => name);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Build the hybrid index from the loaded MCP tool list.
 * Synchronous internally; kept async for API compatibility.
 *
 * @param {Array} tools - MCP tool objects with name, description, inputSchema, tags
 * @returns {Promise<Object>} Hybrid index consumed by searchToolIndex()
 */
export async function buildToolIndex(tools) {
  // ── BM25 index (MiniSearch) ──────────────────────────────────────────────
  const miniSearch = new MiniSearch({
    idField: 'name',
    fields: ['name', 'nameExpanded', 'description', 'schemaKeys', 'tags'],
    storeFields: ['name'],
    tokenize: text => tokenize(text),
    processTerm: term => term,
  });

  const docs = tools.map(tool => ({
    name: tool.name,
    nameExpanded: tokenize(tool.name).join(' '),
    description: tool.description || '',
    schemaKeys: Object.keys(tool.inputSchema?.properties || {}).join(' '),
    tags: (tool.tags || []).join(' '),
  }));

  miniSearch.addAll(docs);

  // ── TF-IDF index ─────────────────────────────────────────────────────────
  const tfidfDocs = tools.map(tool => ({
    name: tool.name,
    tokens: [
      ...tokenize(tool.name),
      ...tokenize(tool.description || ''),
      ...Object.keys(tool.inputSchema?.properties || {}),
      ...(tool.tags || []),
    ],
  }));
  const tfidf = buildTfidfIndex(tfidfDocs);

  return { miniSearch, tfidf, source: 'local' };
}

/**
 * Hybrid search: Name-BM25 + Synonym-expanded BM25 + TF-IDF → weighted RRF → top K.
 *
 * All three retrievers use ONLY the current user query (not prior context) so a
 * previous conversation about tables doesn't pollute results for "run job".
 *
 * @param {Object} index           - From buildToolIndex()
 * @param {string} query           - Current user message
 * @param {Array}  contextMessages - Prior messages (unused in search — kept for API compat)
 * @param {number} [topK=5]
 * @returns {Promise<string[]>} Tool names ordered by descending fused RRF score
 */
export async function searchToolIndex(index, query, contextMessages = [], topK = 5) {
  if (!index || !query?.trim()) return [];

  const queryTokens = tokenize(query);
  if (!queryTokens.length) return [];

  // ── 1. Name-BM25 ─────────────────────────────────────────────────────────
  const nameBm25Names = index.miniSearch
    .search(queryTokens.join(' '), {
      fields: ['name', 'nameExpanded'],
      fuzzy: 0.15,
      prefix: true,
    })
    .map(r => r.name);

  // ── 2. Synonym-expanded BM25 ─────────────────────────────────────────────
  const expandedTokens = expandTokens(queryTokens);
  const expandedQuery = expandedTokens.join(' ');
  const synonymBm25Names = index.miniSearch
    .search(expandedQuery, {
      fuzzy: 0.2,
      prefix: true,
      boost: { name: 5, nameExpanded: 3, description: 1, schemaKeys: 1 },
    })
    .map(r => r.name);

  // ── 3. TF-IDF cosine ─────────────────────────────────────────────────────
  const tfidfNames = index.tfidf.vectors
    .map(({ name, vec }) => ({ name, score: tfidfScore(expandedTokens, vec, index.tfidf.idf) }))
    .filter(t => t.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, topK * 3)
    .map(t => t.name);

  // ── Weighted RRF ─────────────────────────────────────────────────────────
  const fused = reciprocalRankFusion(
    [nameBm25Names, synonymBm25Names, tfidfNames],
    [2, 2, 1],
    topK,
  );

  return fused.length ? fused : synonymBm25Names.slice(0, topK);
}